export interface EnvironmentPreset {
  id: EnvironmentPresetId;
  label: string;
  description: string;
  scene: {
    background: number;
    fog: number;
    fogDensity: number;
  };
  lighting: {
    hemisphereSky: number;
    hemisphereGround: number;
    hemisphereIntensity: number;
    keyColor: number;
    keyIntensity: number;
    dorsalFillColor: number;
    dorsalFillIntensity: number;
    rimColor: number;
    rimIntensity: number;
    interiorColor: number;
    interiorIntensity: number;
  };
  particles: {
    ambientColor: number;
    gustColor: number;
  };
}

export type EnvironmentPresetId =
  | "standard"
  | "clarity-field"
  | "deep-pelagic"
  | "violet-veil"
  | "ember-trench";

export const environmentPresets: readonly EnvironmentPreset[] = [
  {
    id: "standard",
    label: "Standard",
    description: "The original quiet observation field.",
    scene: { background: 0x080b10, fog: 0x080b10, fogDensity: 0.032 },
    lighting: {
      hemisphereSky: 0xa9c7e8,
      hemisphereGround: 0x17121d,
      hemisphereIntensity: 1.25,
      keyColor: 0xffead1,
      keyIntensity: 4.8,
      dorsalFillColor: 0xe8f0ff,
      dorsalFillIntensity: 2.3,
      rimColor: 0x6e9cff,
      rimIntensity: 3.1,
      interiorColor: 0x8dd8d1,
      interiorIntensity: 1.8,
    },
    particles: { ambientColor: 0x91b9c7, gustColor: 0xb7dce2 },
  },
  {
    id: "clarity-field",
    label: "Clarity Field",
    description: "Soft lavender daylight for clear inspection of color and pattern.",
    scene: { background: 0x777b91, fog: 0x9297ad, fogDensity: 0.012 },
    lighting: {
      hemisphereSky: 0xf2efff,
      hemisphereGround: 0x4b5065,
      hemisphereIntensity: 1.8,
      keyColor: 0xfffaf2,
      keyIntensity: 5.4,
      dorsalFillColor: 0xfffcf5,
      dorsalFillIntensity: 4.2,
      rimColor: 0xb9cbff,
      rimIntensity: 2.2,
      interiorColor: 0xe3dcff,
      interiorIntensity: 1.65,
    },
    particles: { ambientColor: 0xd4dcf2, gustColor: 0xf0ecff },
  },
  {
    id: "deep-pelagic",
    label: "Deep Pelagic",
    description: "Jade current in a near-black ocean depth.",
    scene: { background: 0x03110f, fog: 0x06241d, fogDensity: 0.055 },
    lighting: {
      hemisphereSky: 0x2e8b72,
      hemisphereGround: 0x020807,
      hemisphereIntensity: 1.55,
      keyColor: 0x78b88c,
      keyIntensity: 4,
      dorsalFillColor: 0xa9d8c7,
      dorsalFillIntensity: 1.8,
      rimColor: 0x1ed0a0,
      rimIntensity: 2.6,
      interiorColor: 0x5ce0bd,
      interiorIntensity: 1.55,
    },
    particles: { ambientColor: 0x48a98c, gustColor: 0x70c9a7 },
  },
  {
    id: "violet-veil",
    label: "Violet Veil",
    description: "Plum darkness with a slow violet haze.",
    scene: { background: 0x0e0717, fog: 0x24102e, fogDensity: 0.06 },
    lighting: {
      hemisphereSky: 0x8458a8,
      hemisphereGround: 0x100817,
      hemisphereIntensity: 1.4,
      keyColor: 0xd4a1cc,
      keyIntensity: 4.1,
      dorsalFillColor: 0xd8c4ef,
      dorsalFillIntensity: 2,
      rimColor: 0x7d54d8,
      rimIntensity: 3.5,
      interiorColor: 0xc277ce,
      interiorIntensity: 1.7,
    },
    particles: { ambientColor: 0x9b73bd, gustColor: 0xb88bc8 },
  },
  {
    id: "ember-trench",
    label: "Ember Trench",
    description: "Burgundy depth with restrained mineral warmth.",
    scene: { background: 0x120806, fog: 0x30100c, fogDensity: 0.045 },
    lighting: {
      hemisphereSky: 0x9b5738,
      hemisphereGround: 0x120604,
      hemisphereIntensity: 1.25,
      keyColor: 0xe0a065,
      keyIntensity: 4.3,
      dorsalFillColor: 0xe7bd91,
      dorsalFillIntensity: 1.9,
      rimColor: 0x8c2836,
      rimIntensity: 2.8,
      interiorColor: 0xd97855,
      interiorIntensity: 1.4,
    },
    particles: { ambientColor: 0xb86d4e, gustColor: 0xc88767 },
  },
] as const;

export const DEFAULT_ENVIRONMENT = environmentPresets[0];

export function getEnvironmentPreset(id: string): EnvironmentPreset | undefined {
  return environmentPresets.find((preset) => preset.id === id);
}
